import axios from "axios";
import { finishAnonymousHolterUploadV2, listAnonymousHolterMultipartLinksV2 } from "../api";
import { AnonymousUploadPart, AnonymousUploadPartLink } from "../types";
import { AnonymousHolterUploader } from "./AnonymousHolterUploader";

const normalizeEtag = (etag?: string) => etag?.replaceAll('"', "");

export class MultipartHolterUploader extends AnonymousHolterUploader {
  async upload() {
    if (!this.initData.upload_id) {
      throw new Error("Multipart upload ID is missing");
    }

    const { data: linksData } = await listAnonymousHolterMultipartLinksV2({
      serialNumber: this.options.serialNumber,
      sharableCode: this.options.sharableCode,
      uploadId: this.initData.upload_id,
      chunksNumber: this.chunks.length,
    });
    const partUploads: AnonymousUploadPartLink[] = linksData.part_uploads || [];

    if (partUploads.length !== this.chunks.length) {
      throw new Error("Multipart upload links count does not match file chunks count");
    }

    const parts = await this.uploadParts(partUploads);

    return await finishAnonymousHolterUploadV2({
      serialNumber: this.options.serialNumber,
      sharableCode: this.options.sharableCode,
      measurementId: this.options.measurementId,
      uploadId: this.initData.upload_id,
      chunksNumber: this.chunks.length,
      parts,
      fileSize: this.options.file.size,
    });
  }

  private async uploadParts(partUploads: AnonymousUploadPartLink[]): Promise<AnonymousUploadPart[]> {
    const parts = [];

    for (let index = 0; index < this.chunks.length; index++) {
      const partUpload = partUploads[index];

      if (!partUpload?.url) {
        throw new Error(`Multipart upload URL is missing for part ${index + 1}`);
      }

      const response = await this.uploadWithRetry(() =>
        axios.put(partUpload.url, this.chunks[index].blob, {
          headers: {
            "Content-Type": "application/octet-stream",
            ...(partUpload.headers || {}),
          },
        })
      );
      const etag = normalizeEtag(response.headers?.etag || response.headers?.ETag);

      if (!etag) {
        throw new Error(`Missing ETag for uploaded part ${partUpload.part_number}`);
      }

      parts.push({
        part_number: partUpload.part_number,
        etag,
      });

      this.reportProgress(index);
    }

    return parts;
  }
}
